import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import styled from 'styled-components';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';
import AuthService from '../../services/authService';

// Backend routes live under /admin/exam-boards; axiosInstance already prefixes /api
const API_BASE = '/admin/exam-boards';

const statusBadges = {
  current: 'bg-green-100 text-green-800 border border-green-200',
  future: 'bg-blue-100 text-blue-800 border border-blue-200',
  completed: 'bg-gray-100 text-gray-700 border border-gray-200',
};

const softwareSubjectOrder = [
  'PRO', 'NET', 'PP', 'DDD', 'SDLC', 'PACP', 'Security', 'WDD',
  'CRP - I', 'BPS', 'UEID', 'SAD', 'APDP', 'DM', 'DSA', 'CRP - I',
];

const networkSubjectOrder = [
  'PRO', 'NET', 'PP', 'DDD', 'Maths', 'PACP', 'Security', 'CSA',
  'CRP - I', 'BPS', 'TND', 'CC', 'NS', 'IOT', 'ET', 'CRP - II',
];

const markStyles = {
  D: 'bg-emerald-100 text-emerald-800 border border-emerald-300',
  M: 'bg-emerald-100 text-emerald-800 border border-emerald-300',
  P: 'bg-emerald-50 text-emerald-800 border border-emerald-200',
  R: 'bg-rose-100 text-rose-800 border border-rose-300',
  SN: 'bg-amber-50 text-amber-700 border border-amber-200',
  NS: 'bg-slate-100 text-slate-700 border border-slate-200',
};

const submissionStatusOptions = [
  'On-Time Submission',
  'Late Submission',
  'Resubmitted',
  'not submitted',
];

const markOptions = [
  { value: '', label: 'Not Corrected Yet' },
  { value: 'D', label: 'D - Distinction' },
  { value: 'M', label: 'M - Merit' },
  { value: 'P', label: 'P - Pass' },
  { value: 'R', label: 'R - Referred' },
];

const PLACEHOLDER_IMAGE = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="260" viewBox="0 0 200 260"><rect width="200" height="260" fill="%23f1f5f9"/><text x="50%" y="50%" text-anchor="middle" fill="%2394a3b8" font-size="20" font-family="Arial, sans-serif">No Photo</text></svg>';

const PassportWrapper = styled.div`
  width: 90px;
  height: 120px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const PassportImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
  display: block;
`;

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
}

function parseLocalDate(value) {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [y, m, d] = trimmed.split('-').map(Number);
    if ([y, m, d].some((n) => Number.isNaN(n))) return null;
    return new Date(y, m - 1, d);
  }

  if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
    const [datePart] = trimmed.split('T');
    const [y, m, d] = datePart.split('-').map(Number);
    if ([y, m, d].some((n) => Number.isNaN(n))) return null;
    return new Date(y, m - 1, d);
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getUniversityRegisterInfo(value) {
  const parsed = parseLocalDate(value);
  if (!parsed) return null;

  const formattedDate = parsed.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });

  const now = new Date();
  let months = (now.getFullYear() - parsed.getFullYear()) * 12 + (now.getMonth() - parsed.getMonth());
  if (now.getDate() < parsed.getDate()) {
    months -= 1;
  }
  if (months < 0) months = 0;

  const monthsLabel = months === 1 ? '1 month' : `${months} months`;

  return { formattedDate, monthsLabel, monthsCount: months };
}

function buildOptions(items) {
  return items.map((board) => ({
    value: board.examBoardId,
    label: `${board.examBoardName} • ${formatDate(board.date)} (${board.status})`,
  }));
}

function StudentAvatar({ name }) {
  const initials = (name || '?')
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700 font-semibold">
      {initials || '?'}
    </div>
  );
}

function getSubjectsForGroup(students, courseTitle) {
  const map = new Map();
  students.forEach((s) => {
    (s.batchSubjects || []).forEach((bs) => {
      if (bs.subject && !map.has(bs.subject.subjectId)) {
        map.set(bs.subject.subjectId, {
          subjectId: bs.subject.subjectId,
          shortName: bs.subject.shortName,
          fullName: bs.subject.fullName,
          semester: bs.semester || '0',
        });
      }
    });
  });

  const normalizedTitle = (courseTitle || '').toLowerCase();
  const isSoftware = normalizedTitle === 'level 5 pearson btec hnd in computing (software engineering)';
  const isNetwork = normalizedTitle === 'level 5 pearson btec hnd in computing (network engineering)';
  const orderList = isSoftware ? softwareSubjectOrder : (isNetwork ? networkSubjectOrder : null);
  const orderMap = orderList
    ? orderList.reduce((acc, code, idx) => {
        const key = (code || '').trim().toUpperCase();
        if (!(key in acc)) acc[key] = idx;
        return acc;
      }, {})
    : null;

  return Array.from(map.values()).sort((a, b) => {
    if (orderMap) {
      const aKey = (a.shortName || a.fullName || '').trim().toUpperCase();
      const bKey = (b.shortName || b.fullName || '').trim().toUpperCase();
      const aIdx = Object.prototype.hasOwnProperty.call(orderMap, aKey) ? orderMap[aKey] : Number.POSITIVE_INFINITY;
      const bIdx = Object.prototype.hasOwnProperty.call(orderMap, bKey) ? orderMap[bKey] : Number.POSITIVE_INFINITY;
      if (aIdx !== bIdx) return aIdx - bIdx;
    }
    const sa = parseInt(a.semester, 10) || 0;
    const sb = parseInt(b.semester, 10) || 0;
    if (sa !== sb) return sa - sb;
    return (a.shortName || '').localeCompare(b.shortName || '');
  });
}

function getStudentSubjectCell(student, subjectId) {
  const match = (student.batchSubjects || []).find((bs) => bs.subject?.subjectId === subjectId);
  if (!match) return { mark: 'NS', title: 'Not submitted' };
  const submission = match.assignmentSubmission || {};
  let mark = submission.marks || null;
  if (!mark && submission.submission_status && submission.submission_status !== 'not submitted') {
    mark = 'SN';
  }
  return {
    mark: mark || 'NS',
    title: `${match.subject?.shortName || ''} • ${submission.submission_status || 'not submitted'}`.trim(),
  };
}

function getStudentStats(student) {
  const batchSubjects = student?.batchSubjects || [];
  let submitted = 0;
  let total = batchSubjects.length;
  let dCount = 0;
  let mCount = 0;
  let pCount = 0;
  let rCount = 0;
  let snCount = 0;
  let nsCount = 0;

  batchSubjects.forEach((bs) => {
    const submission = bs.assignmentSubmission || {};
    const status = submission.submission_status;
    const marks = submission.marks;

    if (status && status !== 'not submitted') submitted += 1;

    if (marks === 'D') dCount += 1;
    else if (marks === 'M') mCount += 1;
    else if (marks === 'P') pCount += 1;
    else if (marks === 'R') rCount += 1;
    else if (!marks && status && status !== 'not submitted') snCount += 1;
    else nsCount += 1;
  });

  const submissionPercentage = total > 0 ? Math.round((submitted / total) * 100) : 0;
  const attendance = student?.attendanceStats?.attendancePercentage;

  return { total, submitted, submissionPercentage, dCount, mCount, pCount, rCount, snCount, nsCount, attendance };
}

function compareStudentsByMarks(sa, sb, a, b) {
  const descKeys = ['dCount', 'mCount', 'pCount', 'snCount', 'submissionPercentage', 'attendance'];
  for (const key of descKeys) {
    const av = sa[key] ?? -1;
    const bv = sb[key] ?? -1;
    if (av !== bv) return bv - av; // higher first
  }

  const ascKeys = ['rCount', 'nsCount'];
  for (const key of ascKeys) {
    const av = sa[key] ?? Number.MAX_SAFE_INTEGER;
    const bv = sb[key] ?? Number.MAX_SAFE_INTEGER;
    if (av !== bv) return av - bv; // fewer is better
  }

  const nameA = (a.fullName || a.student_id || '').toLowerCase();
  const nameB = (b.fullName || b.student_id || '').toLowerCase();
  return nameA.localeCompare(nameB);
}

function getAttendanceBadge(att) {
  if (att === undefined || att === null || Number.isNaN(att)) {
    return 'bg-slate-100 text-slate-600 border border-slate-200';
  }
  if (att >= 80) return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  if (att >= 60) return 'bg-amber-50 text-amber-700 border border-amber-200';
  return 'bg-rose-50 text-rose-700 border border-rose-200';
}

function buildStudentImageSrc(imagePath) {
  if (!imagePath) return PLACEHOLDER_IMAGE;
  const trimmed = imagePath.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const normalized = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  const baseEndsWithApi = getImageURL('').endsWith('/api');
  const cleaned = baseEndsWithApi && normalized.startsWith('/api/')
    ? normalized.slice(4) // drop leading /api to avoid double /api/api
    : normalized;
  return getImageURL(cleaned);
}

function buildStaffImageSrc(staff) {
  const src = staff?.imagePath || staff?.image || staff?.photo || '';
  return buildStudentImageSrc(src);
}

function groupStudentsByCourse(students) {
  const groups = new Map();
  students.forEach((s) => {
    const title = s.batchDetail?.courseName || s.batchDetail?.batchName || 'Unknown course';
    const key = title.toLowerCase();
    if (!groups.has(key)) {
      groups.set(key, { title, students: [] });
    }
    groups.get(key).students.push(s);
  });
  return Array.from(groups.values());
}

function classifySubmissionBucket(batchSubject) {
  const submission = batchSubject?.assignmentSubmission || {};
  const status = (submission.submission_status || '').toLowerCase();
  const marks = submission.marks;
  if (marks === 'D' || marks === 'M' || marks === 'P') return 'marked';
  if (marks === 'R') return 'repeat';
  if (status && status !== 'not submitted') return 'submittedNotMarked';
  return 'notSubmitted';
}

function buildCourseSubjectSummary(students) {
  const courseGroups = groupStudentsByCourse(students);
  return courseGroups.map((course) => {
    const subjectMap = new Map();
    const lecturerMap = new Map();

    course.students.forEach((student) => {
      (student.batchSubjects || []).forEach((bs) => {
        const subj = bs.subject || {};
        const subjectId = subj.subjectId || subj.id || subj.shortName || subj.fullName;
        if (!subjectId) return;

        if (!subjectMap.has(subjectId)) {
          subjectMap.set(subjectId, {
            subjectId,
            shortName: subj.shortName || subj.fullName || 'Subject',
            fullName: subj.fullName || subj.shortName || 'Subject',
            lecturer: bs.staff?.fullName || 'Lecturer N/A',
            counts: {
              marked: 0,
              repeat: 0,
              submittedNotMarked: 0,
              notSubmitted: 0,
            },
          });
        }

        const record = subjectMap.get(subjectId);
        const bucket = classifySubmissionBucket(bs);
        record.counts[bucket] += 1;

        const lecturerName = bs.staff?.fullName || 'Lecturer N/A';
        if (!lecturerMap.has(lecturerName)) {
          lecturerMap.set(lecturerName, {
            lecturer: lecturerName,
            staffId: bs.staff?.id || bs.staff?._id || bs.staff?.staffId || null,
            staffEmail: bs.staff?.email || bs.staff?.emailAddress || '',
            staffPhone: bs.staff?.telephone || bs.staff?.phone || bs.staff?.mobile || '',
            staffImage: buildStaffImageSrc(bs.staff),
            subjects: new Map(),
            counts: { marked: 0, repeat: 0, submittedNotMarked: 0, notSubmitted: 0 },
          });
        }
        const lecRecord = lecturerMap.get(lecturerName);
        const lecSubjectKey = subjectId;
        if (!lecRecord.subjects.has(lecSubjectKey)) {
          lecRecord.subjects.set(lecSubjectKey, {
            subjectId,
            shortName: subj.shortName || subj.fullName || 'Subject',
            fullName: subj.fullName || subj.shortName || 'Subject',
            counts: { marked: 0, repeat: 0, submittedNotMarked: 0, notSubmitted: 0 },
          });
        }
        const lecSubject = lecRecord.subjects.get(lecSubjectKey);
        lecRecord.counts[bucket] += 1;
        lecSubject.counts[bucket] += 1;
      });
    });

    return {
      title: course.title,
      subjects: Array.from(subjectMap.values()),
      lecturers: Array.from(lecturerMap.values()).map((lec) => ({
        ...lec,
        subjects: Array.from(lec.subjects.values()),
      })),
    };
  });
}

function ManageExamBoardModal({ open, onClose, onSave, initial }) {
  const [form, setForm] = useState(() => ({
    examBoardName: initial?.examBoardName || '',
    date: initial?.date ? initial.date.slice(0, 10) : '',
    status: initial?.status || 'current',
  }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      examBoardName: initial?.examBoardName || '',
      date: initial?.date ? initial.date.slice(0, 10) : '',
      status: initial?.status || 'current',
    });
  }, [initial]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.examBoardName.trim()) {
      Swal.fire('Required', 'Exam board name is required', 'warning');
      return;
    }
    if (!form.date) {
      Swal.fire('Required', 'Date is required', 'warning');
      return;
    }

    try {
      setSaving(true);
      await onSave({
        ...form,
        examBoardId: initial?.examBoardId || null,
      });
      onClose();
    } catch (err) {
      // handled upstream
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">
              {initial ? 'Edit Exam Board' : 'New Exam Board'}
            </h2>
            <p className="text-sm text-slate-600">Create or update HND exam boards.</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Exam Board Name</label>
            <input
              name="examBoardName"
              value={form.examBoardName}
              onChange={handleChange}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              placeholder="2026 Feb graduation"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="text-sm font-medium text-slate-700">Date</label>
              <input
                type="date"
                name="date"
                value={form.date}
                onChange={handleChange}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Status</label>
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              >
                <option value="current">Current</option>
                <option value="future">Future</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${saving ? 'bg-indigo-300' : 'bg-indigo-600 hover:bg-indigo-700'}`}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function HndExamBoard() {
  const [examBoards, setExamBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [students, setStudents] = useState([]);
  const [isLoadingBoards, setIsLoadingBoards] = useState(false);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  const [showAllStatuses, setShowAllStatuses] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBoard, setEditingBoard] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchDrawerOpen, setSearchDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [editModal, setEditModal] = useState({ open: false, student: null, subject: null, batchSubject: null, submission: null });
  const [editStatus, setEditStatus] = useState('not submitted');
  const [editMarks, setEditMarks] = useState('');
  const [savingSubmission, setSavingSubmission] = useState(false);

  const user = AuthService.getUser();
  const staffType = user?.superAdmin?.staff?.type || user?.type || '';
  const isAdmin = staffType.toLowerCase() === 'admin';

  const isInSelectedBoard = (stu) => {
    if (!selectedBoard) return false;
    const sid = stu?.studentId || stu?.student_id || stu?.studentId;
    return students.some((s) => (s.student_id || s.studentId) === sid);
  };

  const findBoardStudent = (stu) => {
    const sid = stu?.studentId || stu?.student_id || stu?.studentId;
    if (!sid) return null;
    return students.find((s) => (s.student_id || s.studentId) === sid) || null;
  };

  const filteredBoards = useMemo(() => {
    if (showAllStatuses) return examBoards;
    return examBoards.filter((b) => b.status === 'current');
  }, [examBoards, showAllStatuses]);

  const options = useMemo(() => buildOptions(filteredBoards), [filteredBoards]);

  const grouped = useMemo(() => {
    if (!students.length) return [];
    const filtered = searchTerm.trim()
      ? students.filter((s) => {
          const q = searchTerm.toLowerCase();
          return (
            (s.fullName || '').toLowerCase().includes(q) ||
            (s.student_id || '').toLowerCase().includes(q) ||
            (s.student_registration_number || '').toLowerCase().includes(q) ||
            (s.batchDetail?.batchName || '').toLowerCase().includes(q) ||
            (s.batchDetail?.courseName || '').toLowerCase().includes(q)
          );
        })
      : students;
    return groupStudentsByCourse(filtered);
  }, [students, searchTerm]);

  const loadBoards = async () => {
    try {
      setIsLoadingBoards(true);
      const { data } = await axiosInstance.get(API_BASE);
      const boards = data.examBoards || data.data || [];
      setExamBoards(boards);
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to load exam boards', 'error');
    } finally {
      setIsLoadingBoards(false);
    }
  };

  const handleRefresh = async () => {
    await loadBoards();
    if (selectedBoard?.value) {
      await loadStudents(selectedBoard.value);
    }
  };

  const loadStudents = async (boardId) => {
    if (!boardId) {
      Swal.fire('Select exam board', 'Please select an exam board first.', 'warning');
      return;
    }
    try {
      setIsLoadingStudents(true);
      const { data } = await axiosInstance.get(`${API_BASE}/${boardId}/students`);
      const studentsList = data.students || data.data || [];

      // If backend indicates this exam board is outside the coordinator's
      // responsibility, show a clear message instead of an empty table.
      if (data.isOutsideResponsibility) {
        setStudents([]);
        Swal.fire(
          'Not under your responsibility',
          'This exam board is not under your responsibility.',
          'info',
        );
        return;
      }

      setStudents(studentsList);
    } catch (err) {
      console.error(err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to fetch students', 'error');
    } finally {
      setIsLoadingStudents(false);
    }
  };

  const handleSaveBoard = async (payload) => {
    const isEdit = Boolean(payload.examBoardId);
    try {
      const request = isEdit
        ? axiosInstance.put(`${API_BASE}/${payload.examBoardId}`, payload)
        : axiosInstance.post(API_BASE, payload);
      const { data } = await request;
      const saved = data.examBoard || data.data || payload;
      if (isEdit) {
        setExamBoards((prev) => prev.map((b) => (b.examBoardId === saved.examBoardId ? saved : b)));
      } else {
        setExamBoards((prev) => [saved, ...prev]);
      }
      Swal.fire('Success', isEdit ? 'Exam board updated' : 'Exam board created', 'success');
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.message || 'Unable to save exam board';
      Swal.fire('Error', message, 'error');
      throw err;
    }
  };

  const handleDeleteBoard = async (board) => {
    const result = await Swal.fire({
      title: 'Delete exam board?',
      text: `"${board.examBoardName}" will be removed permanently.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;

    try {
      await axiosInstance.delete(`${API_BASE}/${board.examBoardId}`);
      setExamBoards((prev) => prev.filter((b) => b.examBoardId !== board.examBoardId));
      if (selectedBoard?.value === board.examBoardId) {
        setSelectedBoard(null);
        setStudents([]);
      }
      Swal.fire('Deleted', 'Exam board removed', 'success');
    } catch (err) {
      console.error(err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to delete exam board', 'error');
    }
  };

  const handleRemoveStudent = async (student) => {
    if (!selectedBoard?.value || !student?.id) {
      Swal.fire('Select exam board', 'Please select an exam board first.', 'warning');
      return;
    }

    const name = student.fullName || student.student_id || 'this student';
    const result = await Swal.fire({
      title: 'Remove student?',
      text: `Remove ${name} from this exam board?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Remove',
    });
    if (!result.isConfirmed) return;

    try {
      await axiosInstance.delete(`${API_BASE}/${selectedBoard.value}/students/${student.id}`);
      setStudents((prev) => prev.filter((s) => s.id !== student.id));
      Swal.fire('Removed', `${name} removed from exam board`, 'success');
    } catch (err) {
      console.error(err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to remove student', 'error');
    }
  };

  const handleAddStudentToBoard = async (stu) => {
    if (!selectedBoard?.value) {
      Swal.fire('Select exam board', 'Please select an exam board first.', 'warning');
      return;
    }
    const studentId = stu?.studentId || stu?.student_id;
    const registrationNumber = stu?.registrationNumber || stu?.student_registration_number || null;
    if (!studentId) {
      Swal.fire('Missing data', 'Student ID is missing; cannot add.', 'warning');
      return;
    }

    try {
      await axiosInstance.post(`${API_BASE}/${selectedBoard.value}/students`, {
        studentId,
        registrationNumber,
        // backend expects snake_case keys; send both to be safe
        student_id: studentId,
        student_registration_number: registrationNumber,
      });
      await loadStudents(selectedBoard.value);
      Swal.fire('Added', `${stu.fullName || studentId} added to exam board`, 'success');
    } catch (err) {
      console.error(err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to add student', 'error');
    }
  };

  const handleOpenSummaryTab = () => {
    if (!selectedBoard) {
      Swal.fire('Select exam board', 'Please select an exam board first.', 'warning');
      return;
    }
    if (!students.length) {
      Swal.fire('No data', 'Load students before opening the summary.', 'info');
      return;
    }

    const courseSummaries = buildCourseSubjectSummary(students);
    const aggregate = courseSummaries.reduce((acc, course) => {
      course.subjects.forEach((subj) => {
        acc.marked += subj.counts.marked;
        acc.repeat += subj.counts.repeat;
        acc.submittedNotMarked += subj.counts.submittedNotMarked;
        acc.notSubmitted += subj.counts.notSubmitted;
      });
      return acc;
    }, { marked: 0, repeat: 0, submittedNotMarked: 0, notSubmitted: 0 });

    const escapeHtml = (val) => String(val ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

    const sanitizeUrl = (url) => (!url ? '' : url.replace(/"/g, '%22'));

    const csvRows = [
      ['Course', 'Subject', 'Lecturer', 'Submitted & Marked (Corrected)', 'Submitted but Repeat', 'Submitted but Not Marked', 'Not Submitted'],
      ...courseSummaries.flatMap((course) => course.subjects.map((subj) => [
        course.title,
        subj.fullName,
        subj.lecturer,
        subj.counts.marked,
        subj.counts.repeat,
        subj.counts.submittedNotMarked,
        subj.counts.notSubmitted,
      ])),
      [''],
      ['Lecturer View'],
      ['Lecturer', 'Course', 'Subject', 'Submitted & Marked (Corrected)', 'Submitted but Repeat', 'Submitted but Not Marked', 'Not Submitted'],
      ...courseSummaries.flatMap((course) => course.lecturers.flatMap((lec) => lec.subjects.map((subj) => [
        lec.lecturer,
        course.title,
        subj.fullName,
        subj.counts.marked,
        subj.counts.repeat,
        subj.counts.submittedNotMarked,
        subj.counts.notSubmitted,
      ]))),
    ];
    const csvContent = csvRows.map((row) => row.map((cell) => {
      const value = cell ?? '';
      if (typeof value === 'number') return value;
      const needsQuotes = /[",\n]/.test(value);
      const escaped = String(value).replace(/"/g, '""');
      return needsQuotes ? `"${escaped}"` : escaped;
    }).join(','))
      .join('\n');
    const csvDataUrl = `data:text/csv;charset=utf-8,${encodeURIComponent(csvContent)}`;

    const buildBar = (count, max, color) => {
      const height = max > 0 ? Math.max(8, (count / max) * 120) : 8;
      return `<div class="bar"><div class="fill" style="height:${height}px;background:${color}"></div><div class="count">${count}</div></div>`;
    };

    const courseSections = courseSummaries.map((course) => {
      const maxCount = Math.max(
        1,
        ...course.subjects.map((s) => Math.max(
          s.counts.marked,
          s.counts.repeat,
          s.counts.submittedNotMarked,
          s.counts.notSubmitted,
        )),
      );
      const cards = course.subjects.map((subj) => {
        const bars = [
          buildBar(subj.counts.marked, maxCount, '#0EA5E9'),
          buildBar(subj.counts.repeat, maxCount, '#F59E0B'),
          buildBar(subj.counts.submittedNotMarked, maxCount, '#A78BFA'),
          buildBar(subj.counts.notSubmitted, maxCount, '#94A3B8'),
        ].join('');
        return `
          <div class="card">
            <div class="card-head">
              <div>
                <div class="card-title" title="${escapeHtml(subj.fullName)}">${escapeHtml(subj.shortName)}</div>
                <div class="card-sub">${escapeHtml(subj.lecturer)}</div>
              </div>
              <div class="badge">${subj.counts.marked + subj.counts.repeat + subj.counts.submittedNotMarked + subj.counts.notSubmitted}</div>
            </div>
            <div class="chart">${bars}</div>
            <div class="legend">
              <span><span class="dot" style="background:#0EA5E9"></span>Submitted & Marked</span>
              <span><span class="dot" style="background:#F59E0B"></span>Submitted but Repeat</span>
              <span><span class="dot" style="background:#A78BFA"></span>Submitted but Not Marked</span>
              <span><span class="dot" style="background:#94A3B8"></span>Not Submitted</span>
            </div>
          </div>
        `;
      }).join('');
      return `
        <section class="course">
          <div class="course-head">
            <div class="course-title">${escapeHtml(course.title)}</div>
            <div class="course-meta">Subjects: ${course.subjects.length}</div>
          </div>
          <div class="card-grid">${cards}</div>
          <div class="lecturer-wrap">
            ${course.lecturers.map((lec) => {
              const lecTotal = lec.counts.marked + lec.counts.repeat + lec.counts.submittedNotMarked + lec.counts.notSubmitted;
              const email = lec.staffEmail ? `<span>${escapeHtml(lec.staffEmail)}</span>` : '';
              const phone = lec.staffPhone ? `<span>${escapeHtml(lec.staffPhone)}</span>` : '';
              const contact = (email || phone) ? `<div class="lec-contact">${email}${phone}</div>` : '';
              const avatar = `<div class="lec-avatar"><img src="${getImageURL(lec.staffImage)}" alt="${escapeHtml(lec.lecturer)}" /></div>`;
              return `
                <div class="lec-card">
                  <div class="lec-head">
                    <div class="lec-left">
                      ${avatar}
                      <div>
                        <div class="lec-title">${escapeHtml(lec.lecturer)}</div>
                        ${contact}
                        <div class="lec-sub">Corrected: ${lec.counts.marked} • Repeat: ${lec.counts.repeat} • Pending Mark: ${lec.counts.submittedNotMarked} • Not Submitted: ${lec.counts.notSubmitted}</div>
                      </div>
                    </div>
                    <div class="badge">${lecTotal}</div>
                  </div>
                  <div class="lec-subjects">
                    ${lec.subjects.map((sub) => {
                      const lecBars = [
                        buildBar(sub.counts.marked, maxCount, '#0EA5E9'),
                        buildBar(sub.counts.repeat, maxCount, '#F59E0B'),
                        buildBar(sub.counts.submittedNotMarked, maxCount, '#A78BFA'),
                        buildBar(sub.counts.notSubmitted, maxCount, '#94A3B8'),
                      ].join('');
                      const subTotal = sub.counts.marked + sub.counts.repeat + sub.counts.submittedNotMarked + sub.counts.notSubmitted;
                      return `
                        <div class="lec-subject-card">
                          <div class="lec-subject-head">
                            <div class="lec-subject-title" title="${escapeHtml(sub.fullName)}">${escapeHtml(sub.shortName)}</div>
                            <div class="badge muted">${subTotal}</div>
                          </div>
                          <div class="chart small">${lecBars}</div>
                        </div>
                      `;
                    }).join('')}
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        </section>
      `;
    }).join('');

    const summaryHtml = `<!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Exam Board Summary</title>
        <style>
          body { font-family: Inter, 'Segoe UI', system-ui, -apple-system, sans-serif; background: #f8fafc; margin: 0; padding: 24px; color: #0f172a; }
          .page { max-width: 1200px; margin: 0 auto; }
          h1 { margin: 0 0 4px; font-size: 24px; }
          .muted { color: #475569; font-size: 13px; margin-bottom: 16px; }
          .summary { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; }
          .pill { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 14px; font-weight: 600; color: #0f172a; box-shadow: 0 1px 2px rgba(15,23,42,0.08); }
          .pill strong { color: #0ea5e9; margin-right: 6px; }
          .actions { display: flex; gap: 12px; margin: 8px 0 20px; align-items: center; }
          .btn { background: #0ea5e9; color: #fff; padding: 10px 14px; border-radius: 8px; font-weight: 700; border: none; cursor: pointer; text-decoration: none; }
          .btn.secondary { background: #e2e8f0; color: #0f172a; }
          .course { margin-bottom: 28px; }
          .course-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
          .course-title { font-size: 18px; font-weight: 700; letter-spacing: -0.01em; }
          .course-meta { color: #475569; font-size: 13px; }
          .card-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 14px; }
          .card { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; box-shadow: 0 1px 3px rgba(15,23,42,0.08); }
          .card-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
          .card-title { font-size: 15px; font-weight: 700; color: #0f172a; }
          .card-sub { font-size: 12px; color: #475569; margin-top: 2px; }
          .badge { background: #0ea5e9; color: #fff; border-radius: 12px; padding: 6px 10px; font-weight: 700; font-size: 12px; }
          .chart { display: grid; grid-template-columns: repeat(4, 1fr); align-items: end; gap: 6px; height: 160px; margin-bottom: 10px; }
          .bar { display: flex; flex-direction: column; align-items: center; gap: 6px; }
          .fill { width: 26px; border-radius: 8px 8px 6px 6px; transition: height 0.2s ease-out; }
          .count { font-size: 12px; color: #0f172a; font-weight: 700; }
          .legend { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 4px 10px; font-size: 12px; color: #475569; }
          .dot { display: inline-block; width: 10px; height: 10px; border-radius: 999px; margin-right: 6px; }
          .lecturer-wrap { margin-top: 14px; display: grid; gap: 12px; }
          .lec-card { background: #fff; border: 1px solid #d9e0ec; border-radius: 14px; padding: 14px; box-shadow: 0 6px 18px rgba(15,23,42,0.08); }
          .lec-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; gap: 12px; }
          .lec-left { display: flex; align-items: center; gap: 10px; }
          .lec-avatar { width: 42px; height: 42px; border-radius: 12px; overflow: hidden; border: 1px solid #e2e8f0; background: #f8fafc; }
          .lec-avatar img { width: 100%; height: 100%; object-fit: cover; object-position: center; display: block; }
          .lec-title { font-size: 15px; font-weight: 700; color: #0f172a; letter-spacing: -0.01em; }
          .lec-sub { font-size: 12px; color: #475569; margin-top: 2px; }
          .lec-contact { font-size: 12px; color: #334155; display: flex; gap: 10px; margin-top: 2px; }
          .lec-subjects { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 10px; }
          .lec-subject-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 10px; box-shadow: inset 0 1px 0 rgba(255,255,255,0.7); }
          .lec-subject-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
          .lec-subject-title { font-size: 13px; font-weight: 700; color: #0f172a; letter-spacing: -0.01em; }
          .chart.small { height: 118px; }
          .badge.muted { background: #e2e8f0; color: #0f172a; }
        </style>
      </head>
      <body>
        <div class="page">
          <h1>Exam Board Summary</h1>
          <div class="muted">${escapeHtml(selectedBoard.label || 'Selected board')} • Generated ${new Date().toLocaleString()}</div>
          <div class="summary">
            <div class="pill"><strong>Marked</strong>${aggregate.marked}</div>
            <div class="pill"><strong>Repeat</strong>${aggregate.repeat}</div>
            <div class="pill"><strong>Submitted not marked</strong>${aggregate.submittedNotMarked}</div>
            <div class="pill"><strong>Not submitted</strong>${aggregate.notSubmitted}</div>
          </div>
          <div class="actions">
            <a class="btn" href="${csvDataUrl}" download="exam-board-summary.csv" target="_blank" rel="noreferrer">Download Excel (CSV)</a>
            <button class="btn secondary" onclick="window.print()">Print</button>
          </div>
          ${courseSections || '<p>No data available.</p>'}
        </div>
      </body>
    </html>`;

    const win = window.open('', '_blank');
    if (!win) {
      Swal.fire('Popup blocked', 'Allow pop-ups to view the summary.', 'warning');
      return;
    }
    win.document.write(summaryHtml);
    win.document.close();
  };

  const openSubmissionModal = (student, subject) => {
    const batchSubject = (student.batchSubjects || []).find((bs) => bs.subject?.subjectId === subject.subjectId);
    if (!batchSubject) return;
    const submission = batchSubject.assignmentSubmission || null;
    setEditModal({ open: true, student, subject, batchSubject, submission });
    setEditStatus(submission?.submission_status || 'not submitted');
    setEditMarks(submission?.marks || '');
  };

  const closeSubmissionModal = () => {
    setEditModal({ open: false, student: null, subject: null, batchSubject: null, submission: null });
    setEditStatus('not submitted');
    setEditMarks('');
    setSavingSubmission(false);
  };

  const handleSaveSubmission = async () => {
    if (!selectedBoard?.value || !editModal.open || !editModal.student || !editModal.batchSubject) {
      Swal.fire('Missing data', 'Select an exam board and a valid subject to edit.', 'warning');
      return;
    }

    const studentId = editModal.student.student_id || editModal.student.studentId;
    const batchSubjectId = editModal.batchSubject.batchSubjectId;
    if (!studentId || !batchSubjectId) {
      Swal.fire('Missing data', 'Student or subject is missing; cannot save.', 'warning');
      return;
    }

    try {
      setSavingSubmission(true);
      const payload = {
        student_id: studentId,
        batch_subject_id: batchSubjectId,
        submission_status: editStatus,
        marks: editMarks || null,
      };
      const { data } = await axiosInstance.put(`${API_BASE}/${selectedBoard.value}/submissions`, payload);
      const saved = data?.submission || {};
      const updatedSubmission = {
        ...(editModal.submission || {}),
        id: saved.id || editModal.submission?.id,
        submission_status: saved.submission_status ?? editStatus,
        marks: saved.marks ?? (editMarks || null),
        batch_subjectId: saved.batch_subjectId || batchSubjectId,
        studentId: saved.studentId || studentId,
        updatedAt: saved.updatedAt || new Date().toISOString(),
      };

      setStudents((prev) => prev.map((s) => {
        const matchById = s.id && editModal.student.id && s.id === editModal.student.id;
        const matchByStudent = s.student_id && editModal.student.student_id && s.student_id === editModal.student.student_id;
        if (!matchById && !matchByStudent) return s;
        const updatedBatchSubjects = (s.batchSubjects || []).map((bs) =>
          bs.batchSubjectId === batchSubjectId
            ? { ...bs, assignmentSubmission: updatedSubmission }
            : bs
        );
        return { ...s, batchSubjects: updatedBatchSubjects };
      }));

      Swal.fire('Saved', 'Assignment submission updated.', 'success');
      closeSubmissionModal();
    } catch (err) {
      console.error(err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to save submission', 'error');
    } finally {
      setSavingSubmission(false);
    }
  };

  const executeStudentSearch = async () => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchError('Type at least 2 characters to search');
      setSearchResults([]);
      return;
    }
    try {
      setSearchLoading(true);
      setSearchError('');
      const { data } = await axiosInstance.get(`${API_BASE}/search-students`, { params: { q } });
      setSearchResults(data.students || data.data || []);
    } catch (err) {
      console.error(err);
      setSearchError(err.response?.data?.message || 'Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    loadBoards();
  }, []);

  return (
    <DashboardLayout title="HND Exam Board">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">HND Exam Board</h1>
            <p className="text-slate-600 text-sm">Select a board and review subject-wise outcomes.</p>
          </div>
          <div className="flex gap-2 items-center">
            {isAdmin && selectedBoard && (
              <button
                type="button"
                onClick={() => {
                  setSearchDrawerOpen(true);
                  setSearchQuery('');
                  setSearchResults([]);
                  setSearchError('');
                }}
                className="h-10 w-10 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-100"
                title="Profile"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M6 20c0-3.3137 2.6863-6 6-6s6 2.6863 6 6" />
                </svg>
              </button>
            )}
            {isAdmin && (
              <button
                onClick={() => setModalOpen(true)}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
              >
                Manage Exam Boards
              </button>
            )}
            <button
              onClick={handleRefresh}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="rounded-xl bg-white shadow-sm border border-slate-200 p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-full max-w-lg">
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Select exam board</label>
              <Select
                value={selectedBoard}
                onChange={(option) => {
                  setSelectedBoard(option);
                  setStudents([]);
                }}
                options={options}
                isLoading={isLoadingBoards}
                placeholder="2026 Feb graduation"
                classNamePrefix="exam-board-select"
              />
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={showAllStatuses}
                onChange={(e) => setShowAllStatuses(e.target.checked)}
                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              Show all exam boards
            </label>
            <button
              onClick={() => loadStudents(selectedBoard?.value)}
              disabled={!selectedBoard || isLoadingStudents}
              className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${
                !selectedBoard || isLoadingStudents
                  ? 'bg-indigo-300 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700'
              }`}
            >
              {isLoadingStudents ? 'Loading...' : 'Show Students'}
            </button>
          </div>

          {isAdmin && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filteredBoards.map((board) => (
                <div
                  key={board.examBoardId}
                  className={`rounded-lg border border-slate-200 p-4 shadow-sm ${
                    selectedBoard?.value === board.examBoardId ? 'ring-2 ring-indigo-200' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-900 truncate" title={board.examBoardName}>
                      {board.examBoardName}
                    </h3>
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      statusBadges[board.status] || statusBadges.completed
                    }`}>
                      {board.status}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-slate-600">{formatDate(board.date)}</p>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs font-medium">
                    <button
                      onClick={() => setSelectedBoard({ value: board.examBoardId, label: board.examBoardName })}
                      className="rounded-lg border border-slate-300 px-3 py-1 text-slate-700 hover:bg-slate-50"
                    >
                      Select
                    </button>
                    <button
                      onClick={() => { setEditingBoard(board); setModalOpen(true); }}
                      className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1 text-amber-700 hover:bg-amber-100"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteBoard(board)}
                      className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1 text-rose-700 hover:bg-rose-100"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              {!filteredBoards.length && (
                <div className="col-span-full rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600">
                  {isLoadingBoards ? 'Loading exam boards...' : 'No exam boards found'}
                </div>
              )}
            </div>
          )}

          {selectedBoard && (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-800">
              <div className="font-semibold">Selected Exam Board</div>
              <div className="flex flex-wrap items-center gap-3 mt-1">
                <span>{selectedBoard.label}</span>
                <span className="rounded-full bg-white/70 px-3 py-1 text-xs font-semibold text-emerald-700">current</span>
              </div>
            </div>
          )}
        </div>

        <div className="rounded-xl bg-white shadow-sm border border-slate-200 p-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Students</h2>
              <p className="text-sm text-slate-600">View subject-wise marks for the selected board.</p>
            </div>
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search in this course..."
              className="w-full max-w-md rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            />
          </div>

          {!selectedBoard && (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600">
              Select an exam board and click "Show Students".
            </div>
          )}

          {selectedBoard && !students.length && !isLoadingStudents && (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600">
              No students loaded yet.
            </div>
          )}

          {isLoadingStudents && (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600">
              Loading students...
            </div>
          )}

          {!isLoadingStudents && grouped.map((group, groupIdx) => {
            const subjects = getSubjectsForGroup(group.students, group.title);
            const statsCache = new Map();
            const getStats = (student) => {
              if (!statsCache.has(student)) {
                statsCache.set(student, getStudentStats(student));
              }
              return statsCache.get(student);
            };
            const sortedStudents = [...group.students].sort((a, b) => compareStudentsByMarks(getStats(a), getStats(b), a, b));
            return (
              <div key={groupIdx} className="space-y-2">
                <div className="flex flex-wrap items-center justify-between rounded-lg bg-slate-900 px-4 py-3 text-sm text-white shadow-sm">
                  <div className="font-semibold">{group.title}</div>
                  <div className="text-slate-200">Students: {group.students.length}</div>
                </div>

                <div className="overflow-x-auto overflow-y-auto max-h-[70vh] rounded-xl border border-slate-200 bg-white shadow-sm">
                  <table className="min-w-max text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th
                          className="sticky left-0 z-20 w-12 min-w-[48px] px-3 py-2 text-left font-semibold bg-slate-50"
                        >
                          #
                        </th>
                        <th
                          className="sticky z-20 w-16 min-w-[64px] px-3 py-2 text-left font-semibold bg-slate-50"
                          style={{ left: 48 }}
                        >
                          FUN
                        </th>
                        <th
                          className="sticky z-20 w-80 min-w-[320px] px-3 py-2 text-left font-semibold bg-slate-50"
                          style={{ left: 112 }}
                        >
                          Identity
                        </th>
                        {subjects.map((subj) => (
                          <th key={subj.subjectId} className="px-2 py-2 text-center font-semibold" title={subj.fullName}>
                            {subj.shortName || subj.fullName || 'Subj'}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {sortedStudents.map((student, idx) => {
                        const stats = getStats(student);
                        const img = buildStudentImageSrc(student.imagePath);
                        const universityId = student.universityID || student.universityId || student.university_id || '';
                        const universityInfo = getUniversityRegisterInfo(
                          student.universityRegisterDate || student.university_register_date
                        );
                        const isEligible = universityInfo && typeof universityInfo.monthsCount === 'number'
                          ? universityInfo.monthsCount >= 12
                          : null;
                        const attBadge = stats.attendance === undefined || stats.attendance === null
                          ? 'bg-slate-100 text-slate-600 border border-slate-200'
                          : stats.attendance >= 80
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : stats.attendance >= 60
                              ? 'bg-amber-50 text-amber-700 border border-amber-200'
                              : 'bg-rose-50 text-rose-700 border border-rose-200';
                        return (
                          <tr key={student.id || student.student_id || idx} className="hover:bg-slate-50">
                            <td
                              className="sticky left-0 z-10 w-12 min-w-[48px] px-3 py-3 align-top text-xs text-slate-500 bg-white"
                            >
                              {idx + 1}
                            </td>
                            <td
                              className="sticky z-10 w-16 min-w-[64px] px-3 py-3 align-top bg-white"
                              style={{ left: 48 }}
                            >
                              <div className="flex flex-col gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => handleRemoveStudent(student)}
                                  className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-600 shadow text-white hover:bg-red-700"
                                  title="Remove"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="h-4 w-4">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12m-9 0v10m6-10v10M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m-9 0h10v12a1 1 0 01-1 1H8a1 1 0 01-1-1V7z" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-600 shadow text-white hover:bg-indigo-700"
                                  title="Archive"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="h-4 w-4">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7l1.664-2.77A2 2 0 016.422 3h11.156a2 2 0 011.758 1.23L21 7M4 7h16M5 7v10a2 2 0 002 2h10a2 2 0 002-2V7M9 11h6" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                            <td
                              className="sticky z-10 w-80 min-w-[320px] px-3 py-3 align-top bg-white"
                              style={{ left: 112 }}
                            >
                              <div className="flex gap-3">
                                <div className="flex flex-col items-center gap-1">
                                  <PassportWrapper>
                                    <PassportImage
                                      src={img}
                                      alt={student.fullName}
                                      loading="lazy"
                                      onError={(e) => {
                                        if (e.currentTarget.src !== PLACEHOLDER_IMAGE) {
                                          e.currentTarget.src = PLACEHOLDER_IMAGE;
                                        }
                                      }}
                                    />
                                  </PassportWrapper>
                                  {isEligible !== null && (
                                    <span
                                      className={`mt-1 inline-flex items-center rounded px-2 py-1 text-[11px] font-semibold border ${
                                        isEligible
                                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                          : 'bg-amber-50 text-amber-700 border-amber-200'
                                      }`}
                                    >
                                      {isEligible ? 'Eligible' : 'Not yet eligible'}
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-slate-700 space-y-0.5">
                                  <div className="text-sm font-semibold text-slate-900">{student.fullName}</div>
                                  <div className="text-slate-600">ID: {student.student_id || ''}</div>
                                  <div className="text-slate-600">Reg: {student.student_registration_number || ''}</div>
                                  <div className="text-slate-600">Tel: {student.telephone || ''}</div>
                                  <div className="text-[11px] text-slate-500">
                                    {student.batchDetail?.batchName || student.batchDetail?.courseName || 'Batch N/A'}
                                  </div>
                                  {(universityId || universityInfo) && (
                                    <div className="text-[11px] text-slate-600 flex flex-wrap gap-1 pt-0.5">
                                      {universityId && <span>Uni ID: {universityId}</span>}
                                      {universityInfo && (
                                        <span>
                                          Uni reg: {universityInfo.formattedDate} • {universityInfo.monthsLabel}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  <div className="flex flex-wrap gap-1.5 pt-1 text-[11px] font-semibold">
                                    <span className={`rounded px-2 py-1 ${attBadge}`}>Att: {stats.attendance ?? 'N/A'}%</span>
                                    <span className="rounded px-2 py-1 bg-slate-100 text-slate-700 border border-slate-200">Sub: {stats.submissionPercentage}%</span>
                                    <span className="rounded px-2 py-1 bg-emerald-100 text-emerald-800 border border-emerald-300">D:{stats.dCount}</span>
                                    <span className="rounded px-2 py-1 bg-emerald-100 text-emerald-800 border border-emerald-300">M:{stats.mCount}</span>
                                    <span className="rounded px-2 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200">P:{stats.pCount}</span>
                                    <span className="rounded px-2 py-1 bg-amber-50 text-amber-700 border border-amber-200">SN:{stats.snCount}</span>
                                    <span className="rounded px-2 py-1 bg-slate-100 text-slate-700 border border-slate-200">NS:{stats.nsCount}</span>
                                  </div>
                                </div>
                              </div>
                            </td>
                            {subjects.map((subj) => {
                              const cell = getStudentSubjectCell(student, subj.subjectId);
                              const cls = markStyles[cell.mark] || markStyles.NS;
                              return (
                                <td
                                  key={`${student.student_id}-${subj.subjectId}`}
                                  className="px-2 py-3 text-center align-middle cursor-pointer transition-colors hover:bg-slate-100"
                                  onDoubleClick={() => openSubmissionModal(student, subj)}
                                >
                                  <span
                                    title={cell.title}
                                    className={`inline-flex h-8 w-10 items-center justify-center rounded ${cls} text-xs font-semibold`}
                                  >
                                    {cell.mark}
                                  </span>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {editModal.open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 py-10">
          <div className="w-full max-w-3xl rounded-xl bg-white shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div className="text-lg font-semibold text-slate-900">Edit Assignment Submission</div>
              <button
                type="button"
                className="text-slate-500 hover:text-slate-700"
                onClick={closeSubmissionModal}
              >
                Close
              </button>
            </div>

            <div className="space-y-4 px-6 py-4">
              <div className="rounded-lg bg-indigo-50 px-4 py-3 text-sm text-slate-800 border border-indigo-100">
                <div className="font-semibold text-indigo-800 mb-1">Student Information</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  <div>Name: {editModal.student?.fullName || '—'}</div>
                  <div>Student ID: {editModal.student?.student_id || editModal.student?.studentId || '—'}</div>
                  <div>Registration No: {editModal.student?.student_registration_number || '—'}</div>
                  <div>Contact: {editModal.student?.telephone || '—'}</div>
                </div>
              </div>

              <div className="rounded-lg bg-emerald-50 px-4 py-3 text-sm text-slate-800 border border-emerald-100">
                <div className="font-semibold text-emerald-800 mb-1">Subject Information</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  <div>Subject: {editModal.subject?.fullName || '—'}</div>
                  <div>Short Name: {editModal.subject?.shortName || '—'}</div>
                  <div>Semester: {editModal.subject?.semester || '—'}</div>
                  <div>Staff: {editModal.batchSubject?.staff?.fullName || '—'}</div>
                </div>
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-semibold text-slate-800">
                  Submission Status <span className="text-rose-500">*</span>
                </label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                  {submissionStatusOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>

                <label className="block text-sm font-semibold text-slate-800">Marks</label>
                <select
                  value={editMarks}
                  onChange={(e) => setEditMarks(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                  {markOptions.map((opt) => (
                    <option key={opt.value || 'empty'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500">Select "Not Corrected Yet" to set marks to null.</p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4 bg-slate-50">
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-white"
                onClick={closeSubmissionModal}
                disabled={savingSubmission}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-60"
                onClick={handleSaveSubmission}
                disabled={savingSubmission}
              >
                {savingSubmission ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ManageExamBoardModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingBoard(null); }}
        onSave={handleSaveBoard}
        initial={editingBoard}
      />

      {isAdmin && searchDrawerOpen && (
        <div className="fixed inset-0 z-[1300] flex">
          <div
            className="flex-1 bg-black/40"
            onClick={() => setSearchDrawerOpen(false)}
            role="presentation"
          />
          <div className="w-full max-w-lg bg-white h-full shadow-2xl border-l border-slate-200 transform translate-x-0 transition-transform duration-200 ease-out">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Search Students</div>
                <div className="text-xs text-slate-500">Search by ID, registration, WhatsApp group, batch, or name.</div>
              </div>
              <button
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
                onClick={() => setSearchDrawerOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="p-4 space-y-3">
              <form
                onSubmit={(e) => { e.preventDefault(); executeStudentSearch(); }}
                className="flex gap-2"
              >
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search student ID, registration number, WhatsApp group, batch name..."
                  className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
                <button
                  type="submit"
                  disabled={searchLoading}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${searchLoading ? 'bg-indigo-300' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                >
                  {searchLoading ? 'Searching...' : 'Search'}
                </button>
              </form>

              {!searchLoading && searchResults.length > 0 && (() => {
                const added = searchResults.filter((r) => isInSelectedBoard(r)).length;
                const notAdded = searchResults.length - added;
                return (
                  <div className="text-xs font-semibold text-slate-600 flex gap-3">
                    <span>Total: {searchResults.length}</span>
                    <span className="text-emerald-700">Added: {added}</span>
                    <span className="text-slate-700">Not added: {notAdded}</span>
                  </div>
                );
              })()}

              {searchError && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">{searchError}</div>
              )}

              {!searchLoading && !searchResults.length && !searchError && (
                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                  Enter a search to see matching students.
                </div>
              )}

              <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
                {searchResults.map((stu) => {
                  const img = buildStudentImageSrc(stu.imagePath);
                  const inBoard = isInSelectedBoard(stu);
                  const boardStudent = findBoardStudent(stu);
                  const stats = boardStudent ? getStudentStats(boardStudent) : (stu.stats || null);
                  const attendanceFromStats = stats ? (stats.attendance ?? stats.attendancePercentage) : null;
                  const attVal = attendanceFromStats !== undefined && attendanceFromStats !== null
                    ? attendanceFromStats
                    : stu.attendancePercentage;
                  const attBadge = getAttendanceBadge(attVal);
                  const submissionPct = stats && stats.submissionPercentage !== undefined && stats.submissionPercentage !== null
                    ? stats.submissionPercentage
                    : 'N/A';
                  const dCount = stats?.dCount ?? 0;
                  const mCount = stats?.mCount ?? 0;
                  const pCount = stats?.pCount ?? 0;
                  const snCount = stats?.snCount ?? 0;
                  const nsCount = stats?.nsCount ?? 0;
                  return (
                    <div key={`${stu.studentId || stu.registrationNumber}`} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                      <div className="flex gap-3">
                        <div className="w-16 h-20 overflow-hidden rounded-md border border-slate-200 bg-slate-50">
                          <img
                            src={img}
                            alt={stu.fullName}
                            className="h-full w-full object-cover object-top"
                            onError={(e) => {
                              if (e.currentTarget.src !== PLACEHOLDER_IMAGE) {
                                e.currentTarget.src = PLACEHOLDER_IMAGE;
                              }
                            }}
                          />
                        </div>
                        <div className="flex-1 space-y-1 text-sm text-slate-700">
                          <div className="font-semibold text-slate-900">{stu.fullName || 'Name N/A'}</div>
                          <div className="text-xs text-slate-600">ID: {stu.studentId || '—'}</div>
                          <div className="text-xs text-slate-600">Reg: {stu.registrationNumber || '—'}</div>
                          <div className="text-xs text-slate-600">Tel: {stu.telephone || '—'}</div>
                          {stu.email && <div className="text-xs text-slate-600">Email: {stu.email}</div>}
                          <div className="rounded border border-indigo-100 bg-indigo-50 px-2 py-1 text-xs text-indigo-700">
                            <div className="font-semibold">{stu.batchName || 'Batch N/A'}</div>
                            <div className="text-[11px]">{stu.batchInternalName || 'Internal N/A'}</div>
                            <div className="text-[11px]">WhatsApp: {stu.whatsapp_group_id || '—'}</div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 pt-1">
                            <span className={`rounded px-2 py-1 text-[11px] font-semibold ${attBadge}`}>
                              Att: {attVal === undefined || attVal === null ? 'N/A' : `${attVal}%`}
                            </span>
                            <span className="rounded bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-700 border border-slate-200">Sub: {submissionPct === 'N/A' ? 'N/A' : `${submissionPct}%`}</span>
                            <span className="rounded bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700 border border-emerald-200">D:{dCount}</span>
                            <span className="rounded bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700 border border-emerald-200">M:{mCount}</span>
                            <span className="rounded bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700 border border-emerald-200">P:{pCount}</span>
                            <span className="rounded bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-700 border border-amber-200">SN:{snCount}</span>
                            <span className="rounded bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-700 border border-slate-200">NS:{nsCount}</span>
                            {inBoard ? (
                              <span className="inline-flex items-center gap-1 rounded border border-blue-200 bg-blue-50 px-3 py-1 text-[12px] font-semibold text-blue-700">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="h-4 w-4">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                Already in Exam Board
                              </span>
                            ) : (
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-3 py-1 text-[12px] font-semibold text-slate-600">
                                  Not in this exam board
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleAddStudentToBoard(stu)}
                                  className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-emerald-700"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="h-4 w-4">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                                  </svg>
                                  Add to Exam Board
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
